// World building: analytic terrain mesh, water, sky/fog/clouds, Kenney asset
// scatter via thin instances, cave towers with numbered landing pads.
import {
  MeshBuilder, StandardMaterial, Color3, Color4, Vector3, Matrix, Quaternion,
  Mesh, VertexData, DynamicTexture, TransformNode, HemisphericLight,
  DirectionalLight, ShadowGenerator, ParticleSystem,
} from '@babylonjs/core';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import '@babylonjs/loaders/glTF';
import {
  terrainHeight, WATER_Y, WORLD_RADIUS, CAVES, HOME, FRUIT_SPOTS, PAD_R, REXES,
  GIANT_TREES, LIANAS, ceilingY,
} from '../shared/level.mjs';
import { mulberry32 } from './rng.js';

const TERRAIN_SIZE = 1280;
const TERRAIN_N = 128;

// One sun for the whole world: the roof shafts, the ground patches, the hall
// rays and the baked contact shadows all lean the same way.
const SUN_DIR = new Vector3(-0.45, -0.8, 0.35).normalize();
function sunAlignQuat() {
  if (!Quaternion.FromUnitVectorsToRef) return Quaternion.Identity();
  const q = new Quaternion();
  Quaternion.FromUnitVectorsToRef(Vector3.Up(), SUN_DIR.scale(-1), q);
  return q;
}

// One colour per cave — the world's only navigation information.
const CAVE_HUE = ['#c2452f', '#d98a2b', '#e0c73c', '#57b048', '#3f9ad1', '#7b57c4', '#c94f9b', '#cfc6ae'];

function flatMat(scene, name, hex, { emissive = 0.1, alpha = 1 } = {}) {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = m.diffuseColor.scale(emissive);
  m.specularColor = new Color3(0.04, 0.04, 0.04);
  if (alpha < 1) m.alpha = alpha;
  return m;
}

// ---------------------------------------------------------------- terrain
function buildTerrain(scene) {
  const n = TERRAIN_N, verts = n + 1, half = TERRAIN_SIZE / 2, step = TERRAIN_SIZE / n;
  const positions = [], indices = [], colors = [];
  // richer jungle floor: two greens + moss + leaf-litter, muted sand, cool rock
  const g1 = Color3.FromHexString('#3f7a33'), g2 = Color3.FromHexString('#559343');
  const moss = Color3.FromHexString('#2e6032'), litter = Color3.FromHexString('#6b7a3a');
  const sand = Color3.FromHexString('#b7a678'), rock = Color3.FromHexString('#6e7a68');
  const deep = Color3.FromHexString('#33604a'), haze = Color3.FromHexString('#67897c');
  const foam = Color3.FromHexString('#cfd9b8');

  for (let iz = 0; iz <= n; iz++) {
    for (let ix = 0; ix <= n; ix++) {
      const x = -half + ix * step, z = -half + iz * step;
      const h = terrainHeight(x, z);
      positions.push(x, h, z);
      const slope = Math.abs(terrainHeight(x + 3, z) - h) / 3 + Math.abs(terrainHeight(x, z + 3) - h) / 3;
      let c;
      if (h < WATER_Y + 0.6) c = deep;
      else if (h < WATER_Y + 1.6) {
        const f = (h - WATER_Y - 0.6) / 1.0;
        c = Color3.Lerp(sand, g1, f);
      } else {
        const noise = 0.5 + 0.5 * Math.sin(x * 0.09 + z * 0.075) * Math.sin(x * 0.031 - z * 0.044);
        c = Color3.Lerp(g1, g2, noise);
        // moss pools in the hollows, dry leaf-litter on small crests
        const patch = Math.sin(x * 0.021 + 2) * Math.cos(z * 0.026 - 1) + Math.sin((x - z) * 0.013);
        if (patch > 0.9) c = Color3.Lerp(c, litter, Math.min(1, (patch - 0.9) * 1.6) * 0.55);
        else if (patch < -0.9) c = Color3.Lerp(c, moss, Math.min(1, (-patch - 0.9) * 1.6) * 0.6);
        const rockiness = Math.min(1, Math.max(0, (slope - 0.55) * 1.8) + Math.max(0, (h - 40) / 30));
        c = Color3.Lerp(c, rock, rockiness);
        // aerial perspective: high rim fades into the green haze
        if (h > 45) c = Color3.Lerp(c, haze, Math.min(0.7, (h - 45) / 40));
      }
      // bright foam line along the water's edge: the river reads as an S-curve
      // from 200 m up, so the splash-crashes stop being blind
      if (h > WATER_Y - 0.4 && h < WATER_Y + 0.35)
        c = Color3.Lerp(c, foam, 0.55 * Math.min(1, slope * 1.6));
      // baked shade under every giant tree — plants the pillars in the ground
      let ao = 0;
      for (const tr of GIANT_TREES) {
        const d = Math.hypot(x - tr.x, z - tr.z);
        if (d < 26) ao = Math.max(ao, (1 - d / 26) * 0.42);
      }
      for (const cv of CAVES) {
        const d = Math.hypot(x - cv.x, z - cv.z);
        if (d < 20) ao = Math.max(ao, (1 - d / 20) * 0.3);
      }
      // baked sky occlusion: the hall is dim except under the roof openings.
      // The 0.62 floor is deliberate — below it the ground between the holes
      // (~130 m apart) turns into an unreadable black smear.
      let sky = 0;
      for (const hh of SKY_HOLES) {
        const R = hh.r * 1.6 + 70;
        const d = Math.hypot(x - hh.x, z - hh.z);
        if (d < R) { const u = 1 - d / R; sky = Math.max(sky, u * u * (3 - 2 * u)); }
      }
      c = c.scale((0.62 + 0.42 * sky) * (1 - ao));
      colors.push(c.r, c.g, c.b, 1);
    }
  }
  for (let iz = 0; iz < n; iz++) {
    for (let ix = 0; ix < n; ix++) {
      const a = iz * verts + ix, b = a + 1, c = a + verts, d = c + 1;
      indices.push(a, b, c, b, d, c);
    }
  }
  const mesh = new Mesh('terrain', scene);
  const vd = new VertexData();
  vd.positions = positions; vd.indices = indices; vd.colors = colors;
  const normals = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  vd.applyToMesh(mesh);
  mesh.convertToFlatShadedMesh();
  const m = new StandardMaterial('terrainMat', scene);
  m.diffuseColor = Color3.White();
  m.specularColor = new Color3(0.02, 0.02, 0.02);
  m.emissiveColor = new Color3(0.085, 0.10, 0.07);
  mesh.material = m;
  mesh.receiveShadows = true;
  mesh.freezeWorldMatrix();
  return mesh;
}

// ---------------------------------------------------------------- assets
// A template is a list of primitive meshes sharing one asset-space; each world
// placement adds the same matrix to every primitive as a thin instance.
// The glTF loader's meshes silently refuse to render as thin instances (both
// merged and per-primitive), so each primitive is REBUILT as a fresh Mesh with
// baked world-space geometry and a StandardMaterial from the PBR base color —
// Kenney nature assets are flat-colored, so nothing is lost.
const assetMatCache = new Map();

// Harmonize Kenney's palette into OUR jungle: the saturated teal foliage
// becomes leaf-green, oranges calm down to warm wood, greys go mossy.
function jungleize(col) {
  const hsv = col.toHSV(); // r = hue 0-360, g = sat, b = val
  let h = hsv.r, s = hsv.g, v = hsv.b;
  if (h >= 130 && h <= 215 && s > 0.25) {
    // teal/cyan foliage → living leaf greens (keep some variation)
    h = 96 + (h - 130) * 0.38;
    s = Math.min(0.75, s * 0.92);
    v = Math.min(0.62, v * 0.82);
  } else if (h >= 85 && h < 130 && s > 0.3) {
    // toy-bright green → deep jungle leaf
    s = Math.min(0.72, s * 0.9);
    v = Math.min(0.52, v * 0.78);
  } else if (h >= 10 && h <= 45 && s > 0.35) {
    // hot Kenney orange → dark warm timber (NOT salmon pastel)
    h *= 0.9;
    s = Math.min(0.7, s * 0.95);
    v *= 0.66;
  } else if (s < 0.15) {
    // grey stone → cool mossy stone. Several Kenney rocks ship with an unset
    // white _defaultMat; without the cap they render as near-white boulders
    // and any half-buried one leaves a blown-out sliver on the jungle floor.
    h = 110; s = 0.08; v = Math.min(0.46, v * 0.88);
  }
  const out = new Color3();
  Color3.HSVtoRGBToRef(h, s, v, out);
  return out;
}

async function loadTemplate(scene, file) {
  const c = await LoadAssetContainerAsync('assets/kenney/' + file, scene);
  c.addAllToScene();
  const srcMeshes = c.meshes.filter(mm => mm.getTotalVertices() > 0);
  const meshes = [];
  let minY = Infinity, maxY = -Infinity, maxR = 0.25;
  for (const src of srcMeshes) {
    src.setParent(null);
    src.bakeCurrentTransformIntoVertices();
    const nm = new Mesh(file.replace('.glb', '') + '_' + meshes.length, scene);
    const vd = new VertexData();
    vd.positions = Array.from(src.getVerticesData('position'));
    vd.normals = Array.from(src.getVerticesData('normal'));
    vd.indices = Array.from(src.getIndices());
    vd.applyToMesh(nm);
    const col = jungleize((src.material && src.material.albedoColor) || Color3.Gray());
    const key = col.toHexString();
    let mat = assetMatCache.get(key);
    if (!mat) {
      mat = new StandardMaterial('asset_' + key, scene);
      mat.diffuseColor = col.clone();
      // downward-facing faces get almost no hemi light, so a small self-lit
      // floor keeps rock undersides and trunk bottoms from going pure black
      mat.emissiveColor = col.scale(0.12);
      mat.specularColor = new Color3(0.03, 0.03, 0.03);
      assetMatCache.set(key, mat);
    }
    nm.material = mat;
    nm.isPickable = false;
    nm.refreshBoundingInfo();
    const bb = nm.getBoundingInfo().boundingBox;
    minY = Math.min(minY, bb.minimumWorld.y);
    maxY = Math.max(maxY, bb.maximumWorld.y);
    maxR = Math.max(maxR, Math.abs(bb.minimumWorld.x), Math.abs(bb.maximumWorld.x),
      Math.abs(bb.minimumWorld.z), Math.abs(bb.maximumWorld.z));
    meshes.push(nm);
  }
  // drop the loader's originals entirely
  for (const src of srcMeshes) src.dispose();
  for (const n of c.transformNodes) n.dispose();
  for (const m of c.meshes) if (!m.isDisposed() && m.getTotalVertices() === 0) m.dispose();
  return {
    meshes,
    _assetHeight: Math.max(0.01, maxY - minY),
    _assetWidth: Math.max(0.01, maxR * 2),
    _assetMinY: minY,
  };
}

function instMatrix(template, m) {
  for (const mesh of template.meshes) mesh.thinInstanceAdd(m, false);
}

function inst(template, x, y, z, yaw, scale) {
  instMatrix(template, Matrix.Compose(
    new Vector3(scale, scale, scale), Quaternion.FromEulerAngles(0, yaw, 0), new Vector3(x, y, z)));
}

// ---------------------------------------------------------------- scatter
function tooCloseToPOI(x, z) {
  if (Math.hypot(x - HOME.x, z - HOME.z) < 22) return true;
  for (const c of CAVES) if (Math.hypot(x - c.x, z - c.z) < 26) return true;
  for (const f of FRUIT_SPOTS) if (Math.hypot(x - f.x, z - f.z) < 5) return true;
  for (const r of REXES) if (Math.hypot(x - r.x, z - r.z) < 28) return true; // rex clearing
  return false;
}

function scatter(rand, templates, spec) {
  const { count, targetH, minH, maxH, maxSlope, shadow = null, shadowScale = 1.25 } = spec;
  let placed = 0, guard = 0;
  while (placed < count && guard++ < count * 12) {
    const a = rand() * Math.PI * 2, r = Math.sqrt(rand()) * (WORLD_RADIUS + 30);
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    const h = terrainHeight(x, z);
    if (h < minH || h > maxH) continue;
    const slope = Math.abs(terrainHeight(x + 3, z) - h) / 3 + Math.abs(terrainHeight(x, z + 3) - h) / 3;
    if (slope > maxSlope) continue;
    if (tooCloseToPOI(x, z)) continue;
    const t = templates[Math.floor(rand() * templates.length)];
    const s = (targetH / t._assetHeight) * (0.75 + rand() * 0.5);
    // Sit the prop on the LOWEST ground under its own footprint, not on the
    // height at its centre: on a slope (and against the 10 m terrain mesh) the
    // centre height leaves a wide flat base hovering with a gap beneath it.
    const fr = Math.min(7, t._assetWidth * 0.5 * s);
    let base = h;
    for (let k = 0; k < 4; k++) {
      const a2 = k / 4 * Math.PI * 2;
      base = Math.min(base, terrainHeight(x + Math.cos(a2) * fr, z + Math.sin(a2) * fr));
    }
    inst(t, x, base - 0.35, z, rand() * Math.PI * 2, s);
    // contact shadow, offset along the sun — skipped on steep ground where a
    // flat disc would hover off the slope
    if (shadow && slope <= 0.5) {
      const sr = Math.min(6.5, t._assetWidth * 0.5 * s * shadowScale);
      shadow.thinInstanceAdd(Matrix.Compose(
        new Vector3(sr, 1, sr), Quaternion.Identity(),
        new Vector3(x + 0.5, h + 0.16, z - 0.4),
      ), false);
    }
    placed++;
  }
}

// ---------------------------------------------------------------- caves
function numberTexture(scene, label, bg = '#7d7466', fg = '#f5ecd8') {
  const dt = new DynamicTexture('num' + label, { width: 256, height: 256 }, scene, true);
  const c = dt.getContext();
  // fill the WHOLE canvas — a plane shows its corners (DynamicTexture starts black)
  c.fillStyle = bg; c.fillRect(0, 0, 256, 256);
  c.fillStyle = 'rgba(0,0,0,0.18)'; c.beginPath(); c.arc(128, 128, 116, 0, Math.PI * 2); c.fill();
  c.strokeStyle = '#4a443b'; c.lineWidth = 12; c.stroke();
  c.fillStyle = fg; c.font = 'bold 150px Georgia';
  c.textAlign = 'center'; c.textBaseline = 'middle';
  c.fillText(label, 128, 138);
  dt.update();
  return dt;
}

function buildCaveTower(scene, cave, tmpl, stoneMat) {
  // every cave owns a colour: the slab, the pad digit and the beacon all wear
  // it, so tower 3 is told apart from tower 6 at 250 m through the fog
  const hue = CAVE_HUE[cave.id - 1] || '#7d7466';
  const hueC = Color3.FromHexString(hue);
  // the two pale hues need dark digits or the number vanishes
  const ink = (hue === '#e0c73c' || hue === '#cfc6ae') ? '#3a3020' : '#f5ecd8';
  // rock pillar: stacked cliff blocks with jittered rotation, ~20 m wide
  const baseY = terrainHeight(cave.x, cave.z);
  const th = cave.y - baseY;
  const block = tmpl.block;
  const s = 20 / block._assetWidth;
  const span = th + 3;                      // from 3 m under terrain up to the pad
  const n = Math.max(2, Math.round(span / (block._assetHeight * s)));
  const bh = span / n;                      // stretch so the stack ends AT the pad
  const sy = bh / block._assetHeight;
  for (let i = 0; i < n; i++) {
    const shrink = 1 - i * 0.35 / n; // slight taper toward the top
    instMatrix(block, Matrix.Compose(
      new Vector3(s * shrink, sy * 1.01, s * shrink),
      Quaternion.FromEulerAngles(0, cave.face + i * Math.PI / 2 + cave.id * 0.7, 0),
      new Vector3(cave.x, baseY - 3 + i * bh, cave.z),
    ));
  }

  // landing platform
  const plat = MeshBuilder.CreateCylinder('plat' + cave.id, { diameter: (PAD_R + 2.5) * 2, height: 2.4, tessellation: 9 }, scene);
  plat.material = stoneMat;
  plat.position.set(cave.x, cave.y - 1.2, cave.z);
  plat.convertToFlatShadedMesh();
  plat.receiveShadows = true;
  plat.freezeWorldMatrix();

  // digit painted on the pad, readable from the air
  const padSign = MeshBuilder.CreateDisc('padSign' + cave.id, { radius: 4.4, tessellation: 24 }, scene);
  const padMat = new StandardMaterial('padSignM' + cave.id, scene);
  padMat.diffuseTexture = numberTexture(scene, String(cave.id), hueC.scale(0.55).toHexString(), ink);
  padMat.emissiveColor = new Color3(0.35, 0.35, 0.3);
  padMat.specularColor = Color3.Black();
  padSign.material = padMat;
  padSign.rotation.x = Math.PI / 2;
  padSign.position.set(cave.x, cave.y + 0.06, cave.z);
  padSign.freezeWorldMatrix();

  // cave mouth behind the pad, facing the valley
  const fwd = new Vector3(Math.sin(cave.face), 0, Math.cos(cave.face));
  const mouthPos = new Vector3(cave.x, cave.y - 0.4, cave.z).subtract(fwd.scale(PAD_R * 0.8));
  const ms = 16 / tmpl.cave._assetWidth;
  instMatrix(tmpl.cave, Matrix.Compose(
    new Vector3(ms, ms, ms),
    Quaternion.FromEulerAngles(0, cave.face, 0),
    mouthPos,
  ));

  // floating number slab above the cave
  const slab = MeshBuilder.CreatePlane('slab' + cave.id, { size: 11, sideOrientation: Mesh.DOUBLESIDE }, scene);
  const slabMat = new StandardMaterial('slabM' + cave.id, scene);
  slabMat.diffuseTexture = numberTexture(scene, String(cave.id), hue, ink);
  slabMat.diffuseTexture.hasAlpha = false;
  slabMat.emissiveColor = hueC.scale(0.72);
  slabMat.specularColor = Color3.Black();
  slab.material = slabMat;
  slab.position.set(cave.x - fwd.x * (PAD_R + 3), cave.y + 15, cave.z - fwd.z * (PAD_R + 3));
  slab.billboardMode = Mesh.BILLBOARDMODE_Y;

  // beacon: a faint column of the cave's own colour from the pad up to the
  // roof. One extra draw call per tower, and the only thing in this world you
  // can navigate by from the far side of the valley.
  const beamH = Math.max(6, ceilingY(cave.x, cave.z) - cave.y - 4);
  const beacon = MeshBuilder.CreateCylinder('beacon' + cave.id, { diameter: 5.5, height: beamH, tessellation: 8 }, scene);
  const beaconMat = new StandardMaterial('beaconM' + cave.id, scene);
  beaconMat.emissiveColor = hueC;
  beaconMat.diffuseColor = Color3.Black();
  beaconMat.alpha = 0.11;
  beaconMat.alphaMode = 1;              // additive — sorts safely against the shafts
  beaconMat.disableLighting = true;
  beaconMat.backFaceCulling = false;
  beacon.material = beaconMat;
  beacon.position.set(cave.x, cave.y + 2 + beamH / 2, cave.z);
  beacon.alphaIndex = 4;
  beacon.isPickable = false;
  beacon.freezeWorldMatrix();

  return { mouthPos, fwd, slabMat };
}

// ---------------------------------------------------------------- fruit templates
export function buildFruitTemplates(scene) {
  const yellow = flatMat(scene, 'f_banana', '#ecc836', { emissive: 0.3 });
  const green = flatMat(scene, 'f_melon', '#4f9c3c', { emissive: 0.25 });
  const brown = flatMat(scene, 'f_meat', '#a5552f', { emissive: 0.25 });
  const bone = flatMat(scene, 'f_bone', '#efe8d8', { emissive: 0.35 });

  const parts = [];
  for (let i = 0; i < 4; i++) {
    const s = MeshBuilder.CreateSphere('bp' + i, { diameter: 0.75, segments: 5 }, scene);
    const a = (i / 3 - 0.5) * 1.6;
    s.position.set(Math.sin(a) * 1.1, Math.cos(a) * 1.1 - 0.6, 0);
    parts.push(s);
  }
  const banana = Mesh.MergeMeshes(parts, true);
  banana.material = yellow; banana.name = 'banana';

  const melon = MeshBuilder.CreateSphere('melon', { diameter: 1.7, segments: 6 }, scene);
  melon.scaling.set(1, 0.82, 1); melon.bakeCurrentTransformIntoVertices();
  melon.material = green;

  const meatParts = [MeshBuilder.CreateCapsule('meat0', { radius: 0.55, height: 2.0, tessellation: 6 }, scene)];
  meatParts[0].rotation.z = Math.PI / 2; meatParts[0].bakeCurrentTransformIntoVertices();
  const meat0 = meatParts[0]; meat0.material = brown;
  const b1 = MeshBuilder.CreateSphere('meatb1', { diameter: 0.5, segments: 4 }, scene);
  b1.position.x = 1.15; b1.material = bone;
  const b2 = b1.clone('meatb2'); b2.position.x = -1.15;
  const meat = Mesh.MergeMeshes([meat0, b1, b2], true, false, undefined, false, true);
  meat.name = 'meat';

  for (const t of [banana, melon, meat]) t.setEnabled(false);
  return { banana, melon, meat };
}

// ---------------------------------------------------------------- leaf roof
// The closed ceiling, clad in crowns: an undulating leaf-surface facing DOWN
// with ragged SKY HOLES where blue sky and slanted sun shafts break through.
// Physically it stays sealed — you can see out, not fly out.
const SKY_HOLES = (() => {
  const rand = mulberry32(0x50111E5);
  const holes = [];
  // One opening straight above every pad, pushed FIRST: the eight destinations
  // go from the darkest spots in the hall to the brightest. They keep a flag so
  // the sun-shaft loop can leave them to the beacons instead.
  for (const c of CAVES) holes.push({ x: c.x, z: c.z, r: 12, cave: true });
  let guard = 0;
  while (holes.length < 20 && guard++ < 800) {
    const a = rand() * Math.PI * 2, rr = Math.sqrt(rand()) * 470;
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr;
    if (CAVES.some(c => Math.hypot(c.x - x, c.z - z) < 42)) continue;
    if (holes.some(h => Math.hypot(h.x - x, h.z - z) < 72)) continue;
    // radius must stay well above the roof grid step (~9 m) or a hole collapses
    // into a single quad and reads as a hard-edged rectangle of sky
    holes.push({ x, z, r: 13 + rand() * 8 });
  }
  if (holes.length !== 20) console.warn('SKY_HOLES: forventede 20 åbninger, fik', holes.length);
  return holes;
})();

function buildRoof(scene) {
  const n = 144, size = 1300, verts = n + 1, half = size / 2, step = size / n;
  const positions = [], indices = [], colors = [];
  const g1 = Color3.FromHexString('#27562b'), g2 = Color3.FromHexString('#3d7639');
  const g3 = Color3.FromHexString('#1b4020'), sunny = Color3.FromHexString('#8fbf58');
  for (let iz = 0; iz <= n; iz++) {
    for (let ix = 0; ix <= n; ix++) {
      const x = -half + ix * step, z = -half + iz * step;
      // leafy bumps hanging under the analytic ceiling
      const bump = 2.2 * Math.sin(x * 0.05 + z * 0.031) * Math.cos(z * 0.043 - x * 0.017);
      positions.push(x, ceilingY(x, z) + 1.5 + bump, z);
      // two octaves: broad crown-sized patches plus a finer break-up, so the
      // ceiling has readable figure-ground instead of one flat dark mass
      const noise = 0.5 + 0.5 * Math.sin(x * 0.07 - z * 0.055) * Math.cos(x * 0.023 + z * 0.061);
      const fine = 0.5 + 0.5 * Math.sin(x * 0.16 + z * 0.13) * Math.cos(x * 0.11 - z * 0.19);
      let c = Color3.Lerp(g1, g2, Math.min(1, noise * 0.75 + fine * 0.45));
      if (Math.sin(x * 0.013 + z * 0.017) > 0.55) c = Color3.Lerp(c, g3, 0.6);
      // sunlight bleeding in around the holes
      for (const h of SKY_HOLES) {
        const d = Math.hypot(x - h.x, z - h.z);
        if (d < h.r + 22) c = Color3.Lerp(sunny, c, Math.max(0, (d - h.r) / 22));
      }
      colors.push(c.r, c.g, c.b, 1);
    }
  }
  // Ragged edge: the radius wobbles with the angle, so a hole never reads as
  // the grid quad it was cut from. Cave holes are NOT cut — ceilingY() is a
  // smooth analytic surface with no knowledge of holes, so an open hole above
  // a pad would be 20 m of visible sky behind an invisible wall.
  const holeR = (h, dx, dz) => {
    const a2 = Math.atan2(dz, dx);
    return h.r * (1 + 0.20 * Math.sin(3 * a2 + h.x * 0.11) + 0.11 * Math.sin(5 * a2 - h.z * 0.09));
  };
  const inHole = (x, z) => SKY_HOLES.some(h =>
    !h.cave && Math.hypot(x - h.x, z - h.z) < holeR(h, x - h.x, z - h.z));
  for (let iz = 0; iz < n; iz++) {
    for (let ix = 0; ix < n; ix++) {
      const cx = -half + (ix + 0.5) * step, cz = -half + (iz + 0.5) * step;
      if (inHole(cx, cz)) continue; // ragged gap — sky shows through
      const a = iz * verts + ix, b = a + 1, c = a + verts, d = c + 1;
      indices.push(a, c, b, b, c, d); // reversed winding: faces DOWN
    }
  }
  const mesh = new Mesh('leafRoof', scene);
  const vd = new VertexData();
  vd.positions = positions; vd.indices = indices; vd.colors = colors;
  const normals = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  vd.applyToMesh(mesh);
  mesh.convertToFlatShadedMesh();
  const m = new StandardMaterial('leafRoofMat', scene);
  m.diffuseColor = Color3.White();
  m.specularColor = Color3.Black();
  m.emissiveColor = new Color3(0.22, 0.28, 0.19); // never pitch black overhead
  mesh.material = m;
  mesh.freezeWorldMatrix();
  mesh.isPickable = false;

  // --- crown cladding: hundreds of leaf-blobs packed along the underside so
  // the ceiling reads as tree crowns, not a surface
  const rand = mulberry32(0xB10B5);
  const cladMats = ['#255226', '#2d5c2b', '#1e4622', '#33693a'].map((hex, i) =>
    flatMat(scene, 'cladMat' + i, hex, { emissive: 0.14 }));
  const cladTmpls = cladMats.map((cm, i) => {
    const b = MeshBuilder.CreateSphere('cladBlob' + i, { diameter: 1, segments: 4 }, scene);
    b.convertToFlatShadedMesh();
    b.material = cm; b.isPickable = false;
    return b;
  });
  let placed = 0, guard = 0;
  while (placed < 340 && guard++ < 2000) {
    const a = rand() * Math.PI * 2, rr = Math.sqrt(rand()) * 560;
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr;
    const s = 8 + rand() * 9;
    if (SKY_HOLES.some(h => Math.hypot(h.x - x, h.z - z) < h.r + s * 0.35)) continue;
    const t = cladTmpls[Math.floor(rand() * cladTmpls.length)];
    t.thinInstanceAdd(Matrix.Compose(
      new Vector3(s, s * 0.42, s),
      Quaternion.FromEulerAngles(0, rand() * Math.PI, 0),
      new Vector3(x, ceilingY(x, z) + 0.8, z),
    ), false);
    placed++;
  }
  for (const t of cladTmpls) {
    if (t.thinInstanceCount > 0) {
      t.thinInstanceBufferUpdated('matrix');
      t.thinInstanceRefreshBoundingInfo();
    } else t.setEnabled(false);
  }

  // --- slanted sun shafts falling through the holes + bright ground patches
  const sunDir = SUN_DIR;
  const alignToSun = sunAlignQuat();
  const shafts = [];
  for (const h of SKY_HOLES) {
    if (h.cave) continue; // the pads get a coloured beacon column instead
    const gy = Math.max(terrainHeight(h.x, h.z), WATER_Y);
    const top = ceilingY(h.x, h.z);
    const H = top - gy;
    if (H < 12) continue;
    // a narrow cone: the old h.r*3.2 base filled a third of the screen up close
    // and read as a sand-coloured wall rather than as light
    const shaft = MeshBuilder.CreateCylinder('shaft', { height: H, diameterTop: h.r * 1.2, diameterBottom: h.r * 2.0, tessellation: 12 }, scene);
    const sm = new StandardMaterial('shaftM', scene);
    sm.emissiveColor = Color3.FromHexString('#fff3c0');
    sm.diffuseColor = Color3.Black();
    sm.alpha = 0.055;
    sm.alphaMode = 1; // additive glow
    sm.disableLighting = true;
    sm.backFaceCulling = false;
    shaft.material = sm;
    shaft.rotationQuaternion = alignToSun;
    // top of the slanted shaft sits in the hole
    shaft.position = new Vector3(h.x, top, h.z).add(sunDir.scale(H / 2));
    shaft.isPickable = false;
    const patch = MeshBuilder.CreateDisc('sunPatch', { radius: h.r * 1.6, tessellation: 20 }, scene);
    const pm = new StandardMaterial('sunPatchM', scene);
    pm.emissiveColor = Color3.FromHexString('#f5e9a8');
    pm.diffuseColor = Color3.Black();
    pm.alpha = 0.13; pm.alphaMode = 1; pm.disableLighting = true;
    patch.material = pm;
    patch.rotation.x = Math.PI / 2;
    // the patch lands ~31 m away along the sun, so it must sit on the terrain
    // THERE, not under the hole — and high enough to clear its own footprint
    const bot = new Vector3(h.x, top, h.z).add(sunDir.scale(H / (-sunDir.y)));
    let py = Math.max(terrainHeight(bot.x, bot.z), WATER_Y);
    for (let k = 0; k < 6; k++) {
      const a2 = k / 6 * Math.PI * 2, rr = h.r * 1.6 * 0.7;
      py = Math.max(py, terrainHeight(bot.x + Math.cos(a2) * rr, bot.z + Math.sin(a2) * rr));
    }
    patch.position.set(bot.x, py + 0.35, bot.z);
    patch.isPickable = false;
    // fadeR: horizontal distance at which the cone starts dimming, so flying
    // INTO a shaft never paints the whole screen beige
    shafts.push({ sm, pm, x: shaft.position.x, z: shaft.position.z, fadeR: h.r });
  }
  return { shafts };
}

// ---------------------------------------------------------------- canopy
// The giant trees: fat tapered trunks and crowns of overlapping leaf-blobs
// forming the broken ceiling the whole game now lives under.
// Soft contact shadow template — ONE mesh, shared by the giant trees and by
// the scattered palms/trees/rocks. The caller uploads the matrix buffer once,
// after every placement is in (see buildWorld).
function buildShadowDisc(scene) {
  const shadowDisc = MeshBuilder.CreateDisc('trunkShadow', { radius: 1, tessellation: 20 }, scene);
  shadowDisc.rotation.x = Math.PI / 2;
  shadowDisc.bakeCurrentTransformIntoVertices();
  const sdMat = new StandardMaterial('trunkShadowMat', scene);
  sdMat.diffuseColor = Color3.Black();
  sdMat.emissiveColor = Color3.Black();
  sdMat.disableLighting = true;
  // soft radial falloff instead of a hard-edged polygon
  const sdt = new DynamicTexture('trunkShadowTex', { width: 64, height: 64 }, scene, false);
  const sctx = sdt.getContext();
  const grad = sctx.createRadialGradient(32, 32, 4, 32, 32, 31);
  grad.addColorStop(0, 'rgba(255,255,255,0.30)');   // 910 discs now, not 200
  grad.addColorStop(0.65, 'rgba(255,255,255,0.16)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  sctx.clearRect(0, 0, 64, 64);
  sctx.fillStyle = grad; sctx.beginPath(); sctx.arc(32, 32, 31, 0, Math.PI * 2); sctx.fill();
  sdt.update(); sdt.hasAlpha = true;
  sdMat.opacityTexture = sdt;
  shadowDisc.material = sdMat;
  shadowDisc.isPickable = false;
  return shadowDisc;
}

function buildCanopy(scene, shadowDisc) {
  const bark = flatMat(scene, 'canopyBark', '#6b4a2a', { emissive: 0.08 });
  const barkPalm = flatMat(scene, 'canopyBarkP', '#8a5f33', { emissive: 0.08 });
  const leafOak = flatMat(scene, 'canopyOak', '#2e6b2f', { emissive: 0.07 });
  const leafPalm = flatMat(scene, 'canopyPalm', '#2f7d52', { emissive: 0.07 });

  function trunkTemplate(mat, name) {
    const m = MeshBuilder.CreateCylinder(name, { height: 1, diameterTop: 0.55, diameterBottom: 1.25, tessellation: 8 }, scene);
    m.position.y = 0.5; m.bakeCurrentTransformIntoVertices(); // pivot at base
    m.convertToFlatShadedMesh();
    m.material = mat; m.isPickable = false;
    return m;
  }
  function blobTemplate(mat, name) {
    const m = MeshBuilder.CreateSphere(name, { diameter: 1, segments: 5 }, scene);
    m.convertToFlatShadedMesh();
    m.material = mat; m.isPickable = false;
    return m;
  }
  const trunks = { palm: trunkTemplate(barkPalm, 'gtrunkP'), oak: trunkTemplate(bark, 'gtrunkO') };
  const blobs = { palm: blobTemplate(leafPalm, 'gcrownP'), oak: blobTemplate(leafOak, 'gcrownO') };

  for (const t of GIANT_TREES) {
    const rnd = mulberry32(t.seed);
    trunks[t.kind].thinInstanceAdd(Matrix.Compose(
      new Vector3(t.trunkR * 2, t.h + 5, t.trunkR * 2),
      Quaternion.FromEulerAngles(0, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.06),
      new Vector3(t.x, t.baseY - 2, t.z),
    ), false);
    const cy = t.baseY + t.h;
    const nBlobs = t.kind === 'palm' ? 7 : 6;
    for (let i = 0; i < nBlobs; i++) {
      const a = (i / nBlobs) * Math.PI * 2 + rnd();
      const d = i === 0 ? 0 : (0.3 + rnd() * 0.35) * t.r;
      const bw = t.r * (i === 0 ? 1.05 : 0.75 + rnd() * 0.3);
      blobs[t.kind].thinInstanceAdd(Matrix.Compose(
        new Vector3(bw, bw * (t.kind === 'palm' ? 0.32 : 0.42), bw),
        Quaternion.FromEulerAngles(0, rnd() * Math.PI, 0),
        new Vector3(t.x + Math.cos(a) * d, cy + (rnd() - 0.4) * t.r * 0.22, t.z + Math.sin(a) * d),
      ), false);
    }
  }

  // lianas: hanging vine ropes with a leaf tuft at the tip
  const vine = MeshBuilder.CreateCylinder('liana', { height: 1, diameter: 0.5, tessellation: 5 }, scene);
  vine.position.y = -0.5; vine.bakeCurrentTransformIntoVertices(); // pivot at TOP
  vine.material = flatMat(scene, 'lianaMat', '#4c6b28', { emissive: 0.1 });
  vine.isPickable = false;
  const tuft = MeshBuilder.CreateSphere('lianaTuft', { diameter: 1, segments: 4 }, scene);
  tuft.convertToFlatShadedMesh();
  tuft.material = flatMat(scene, 'lianaTuftMat', '#3d7d3c', { emissive: 0.1 });
  tuft.isPickable = false;
  for (const l of LIANAS) {
    vine.thinInstanceAdd(Matrix.Compose(
      new Vector3(1, l.len, 1),
      Quaternion.FromEulerAngles(Math.sin(l.phase) * 0.05, 0, Math.cos(l.phase) * 0.05),
      new Vector3(l.x, l.topY, l.z),
    ), false);
    tuft.thinInstanceAdd(Matrix.Compose(
      new Vector3(1.7, 2.4, 1.7),
      Quaternion.Identity(),
      new Vector3(l.x + Math.sin(l.phase) * l.len * -0.05, l.topY - l.len, l.z + Math.cos(l.phase) * l.len * -0.05),
    ), false);
  }

  for (const m of [trunks.palm, trunks.oak, blobs.palm, blobs.oak, vine, tuft]) {
    if (m.thinInstanceCount > 0) {
      m.thinInstanceBufferUpdated('matrix');
      m.thinInstanceRefreshBoundingInfo();
    } else m.setEnabled(false);
  }

  // contact shadows for the pillars — the buffer upload happens in buildWorld,
  // once, AFTER the scatter has added its own 710 discs to the same mesh
  for (const t of GIANT_TREES) {
    const r = t.trunkR * 3.1;
    shadowDisc.thinInstanceAdd(Matrix.Compose(
      new Vector3(r, 1, r), Quaternion.Identity(),
      new Vector3(t.x + 0.8, t.baseY + 0.22, t.z - 0.6),
    ), false);
  }

  // physics data: hard trunk cylinders + soft crown ellipsoids
  return {
    trunkColliders: GIANT_TREES.map(t => ({ x: t.x, z: t.z, r: t.trunkR + 0.8, topY: t.baseY + t.h - 3 })),
    crowns: GIANT_TREES.map(t => ({ x: t.x, y: t.baseY + t.h, z: t.z, r: t.r * 0.85, ry: t.r * 0.38 })),
  };
}

// Drifting pollen motes — dust dancing in the sun shafts around the player.
function buildMotes(scene) {
  const dt = new DynamicTexture('moteTex', { width: 16, height: 16 }, scene, false);
  const c = dt.getContext();
  c.fillStyle = '#fff'; c.beginPath(); c.arc(8, 8, 6, 0, Math.PI * 2); c.fill();
  dt.update(); dt.hasAlpha = true;
  const ps = new ParticleSystem('motes', 140, scene);
  ps.particleTexture = dt;
  ps.emitter = new Vector3(0, 20, 0);
  ps.minEmitBox = new Vector3(-45, -14, -45);
  ps.maxEmitBox = new Vector3(45, 18, 45);
  ps.color1 = new Color4(1, 0.95, 0.7, 0.16);
  ps.color2 = new Color4(1, 0.9, 0.6, 0.1);
  ps.colorDead = new Color4(1, 0.95, 0.7, 0);
  ps.minSize = 0.12; ps.maxSize = 0.3;
  ps.minLifeTime = 5; ps.maxLifeTime = 9;
  ps.emitRate = 16;
  ps.direction1 = new Vector3(-0.5, -0.25, -0.5);
  ps.direction2 = new Vector3(0.5, 0.1, 0.5);
  ps.gravity = new Vector3(0, -0.05, 0);
  ps.start();
  return ps;
}

// ---------------------------------------------------------------- light shafts
// Slanted rays standing in the hall between the pillars. All of them share the
// sun's quaternion — it is the parallelism that sells it, not the count.
// One template, one draw call.
function buildLightShafts(scene) {
  const ray = MeshBuilder.CreateCylinder('lightShaft', { height: 1, diameterTop: 1, diameterBottom: 2.6, tessellation: 6 }, scene);
  ray.position.y = -0.5;
  ray.bakeCurrentTransformIntoVertices();   // pivot at the TOP: it hangs down-sun
  const rayMat = new StandardMaterial('lightShaftM', scene);
  rayMat.emissiveColor = Color3.FromHexString('#e8f0b8');
  rayMat.diffuseColor = Color3.Black();
  rayMat.alpha = 0.055;                     // above ~0.09 the picture turns milky
  rayMat.alphaMode = 1;
  rayMat.disableLighting = true;
  rayMat.backFaceCulling = false;
  ray.material = rayMat;
  ray.isPickable = false;
  ray.alphaIndex = 3;

  const rand = mulberry32(0x5A1B);
  const q = sunAlignQuat();
  let placed = 0, guard = 0;
  while (placed < 40 && guard++ < 1200) {
    const a = rand() * Math.PI * 2, rr = Math.sqrt(rand()) * 500;
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr;
    if (GIANT_TREES.some(t => Math.hypot(t.x - x, t.z - z) < t.r)) continue;  // inside a crown
    if (SKY_HOLES.some(h => Math.hypot(h.x - x, h.z - z) < 30)) continue;     // the holes own their own shafts
    const top = ceilingY(x, z);
    const H = top - Math.max(terrainHeight(x, z), WATER_Y);
    if (H < 12) continue;
    const w = 2.5 + rand() * 3.5;
    ray.thinInstanceAdd(Matrix.Compose(
      new Vector3(w, H, w), q, new Vector3(x, top, z),
    ), false);
    placed++;
  }
  ray.thinInstanceBufferUpdated('matrix');
  ray.thinInstanceRefreshBoundingInfo();
  return { ray, rayMat };
}

// ---------------------------------------------------------------- clouds
function buildClouds(scene, rand) {
  const mat = flatMat(scene, 'cloudMat', '#ffffff', { emissive: 0.42 });
  const blob = MeshBuilder.CreateSphere('cloudBlob', { diameter: 1, segments: 5 }, scene);
  blob.material = mat;
  blob.isPickable = false;
  for (let i = 0; i < 12; i++) {
    const cx = (rand() - 0.5) * 1600, cz = (rand() - 0.5) * 1600;
    const cy = 130 + rand() * 70, cs = 25 + rand() * 30;
    for (let b = 0; b < 4; b++) {
      blob.thinInstanceAdd(Matrix.Compose(
        new Vector3(cs * (0.8 + rand() * 0.6), cs * 0.45, cs * (0.7 + rand() * 0.4)),
        Quaternion.Identity(),
        new Vector3(cx + (b - 1.5) * cs * 0.7, cy + (b % 2) * cs * 0.18, cz + (rand() - 0.5) * cs * 0.5),
      ), false);
    }
  }
  blob.thinInstanceBufferUpdated('matrix');   // 48 > 32 only got away with it by luck
  blob.thinInstanceRefreshBoundingInfo();
  return blob;
}

// ---------------------------------------------------------------- main entry
export async function buildWorld(scene) {
  // dim green hall-light — but the sky peeks through the roof holes,
  // so the clear color is real daylight blue
  scene.clearColor = Color4.FromHexString('#a8d8eaff');
  scene.fogMode = 2; // EXP2
  // these four MUST match SKY.clear in weather.js, which rewrites them per frame
  scene.fogDensity = 0.0015;
  scene.fogColor = Color3.FromHexString('#7fa287');

  // the hall is DIM: the light you get comes through the roof openings, and
  // the terrain bake already carries most of it
  const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
  hemi.intensity = 0.62;
  hemi.diffuse = Color3.FromHexString('#cfe4c0');
  hemi.groundColor = Color3.FromHexString('#42604a');
  const sun = new DirectionalLight('sun', SUN_DIR.clone(), scene);
  sun.intensity = 0.85;
  sun.diffuse = Color3.FromHexString('#fff2d8');
  sun.position = new Vector3(300, 400, -200);
  const shadows = new ShadowGenerator(1024, sun);
  shadows.useExponentialShadowMap = true;

  buildTerrain(scene);

  const water = MeshBuilder.CreateDisc('water', { radius: 900, tessellation: 48 }, scene);
  water.rotation.x = Math.PI / 2;
  water.position.y = WATER_Y;
  const wm = flatMat(scene, 'waterMat', '#14504c', { emissive: 0.1, alpha: 0.9 });
  // wet glint from the shafts — kept tight and dim: a broad highlight on a
  // 900 m disc blows out into white slivers wherever the river shows edge-on
  wm.specularColor = new Color3(0.14, 0.16, 0.14);
  wm.specularPower = 48;
  wm.backFaceCulling = false;
  water.material = wm;

  const rand = mulberry32(19740401);
  const roof = buildRoof(scene);
  // clouds drifting past above the roof — own seed, so adding them does not
  // shift every scattered tree in the world
  buildClouds(scene, mulberry32(0xC10D5));
  const rays = buildLightShafts(scene);

  // load Kenney templates
  const files = {
    palms: ['tree_palm.glb', 'tree_palmBend.glb', 'tree_palmTall.glb', 'tree_palmDetailedTall.glb', 'tree_palmDetailedShort.glb'],
    trees: ['tree_fat.glb', 'tree_detailed.glb'],
    bushes: ['plant_bushDetailed.glb', 'plant_bushLarge.glb', 'plant_flatTall.glb'],
    grass: ['grass_leafsLarge.glb', 'grass_large.glb'],
    flowers: ['flower_purpleA.glb', 'flower_yellowA.glb', 'mushroom_red.glb'],
    rocks: ['rock_largeD.glb', 'rock_largeF.glb', 'rock_tallB.glb'],
    lily: ['lily_large.glb', 'lily_small.glb'],
    block: ['cliff_block_rock.glb'],
    cave: ['cliff_cave_rock.glb'],
  };
  const tmpl = {};
  await Promise.all(Object.entries(files).map(async ([k, list]) => {
    tmpl[k] = await Promise.all(list.map(f => loadTemplate(scene, f)));
  }));

  // Kenney's cliff block wears a bright grass cap. Stacked into a stone cave
  // tower that green slab reads as a misplaced decal, so the block's foliage
  // primitives get their own muted rock-green material. The shared cache is
  // keyed by colour, so a fresh material here leaves every other asset alone.
  for (const m of tmpl.block[0].meshes) {
    const c = m.material && m.material.diffuseColor;
    if (!c || c.g < c.r * 1.25 || c.g < c.b * 1.25) continue;
    const cap = new StandardMaterial('blockCapMat', scene);
    cap.diffuseColor = Color3.Lerp(c, Color3.FromHexString('#6a6a58'), 0.88);
    cap.emissiveColor = cap.diffuseColor.scale(0.12);
    cap.specularColor = new Color3(0.03, 0.03, 0.03);
    m.material = cap;
  }

  // only the big silhouettes get a contact shadow; grass and flowers would
  // just smear the floor
  const shadowDisc = buildShadowDisc(scene);
  scatter(rand, tmpl.palms, { count: 420, targetH: 15, minH: WATER_Y + 0.8, maxH: 26, maxSlope: 0.75, shadow: shadowDisc, shadowScale: 1.25 });
  scatter(rand, tmpl.trees, { count: 200, targetH: 12, minH: WATER_Y + 2.5, maxH: 34, maxSlope: 0.6, shadow: shadowDisc, shadowScale: 1.25 });
  scatter(rand, tmpl.bushes, { count: 280, targetH: 3.2, minH: WATER_Y + 1, maxH: 40, maxSlope: 0.9 });
  scatter(rand, tmpl.grass, { count: 340, targetH: 2.2, minH: WATER_Y + 0.8, maxH: 42, maxSlope: 1.0 });
  scatter(rand, tmpl.flowers, { count: 140, targetH: 1.6, minH: WATER_Y + 1, maxH: 30, maxSlope: 0.8 });
  scatter(rand, tmpl.rocks, { count: 90, targetH: 4.5, minH: WATER_Y + 0.8, maxH: 60, maxSlope: 2, shadow: shadowDisc, shadowScale: 1.25 });

  // lilies on the water along the river
  for (let i = 0; i < 60; i++) {
    const z = (rand() - 0.5) * 900;
    const x = (rand() - 0.5) * 60 + (90 * Math.sin(z * 0.004) + 30 * Math.sin(z * 0.011 + 2.1));
    if (terrainHeight(x, z) < WATER_Y - 0.5) {
      const t = tmpl.lily[Math.floor(rand() * tmpl.lily.length)];
      inst(t, x, WATER_Y + 0.05, z, rand() * Math.PI * 2, 3 / t._assetHeight * (0.6 + rand() * 0.8));
    }
  }

  // caves + home pad
  const stoneMat = flatMat(scene, 'stoneMat', '#8b8478', { emissive: 0.16 });
  const caveInfo = new Map();
  for (const c of CAVES) caveInfo.set(c.id, buildCaveTower(scene, c, { block: tmpl.block[0], cave: tmpl.cave[0] }, stoneMat));

  const homeBaseH = HOME.y - terrainHeight(HOME.x, HOME.z) + 4;
  const homeBase = MeshBuilder.CreateCylinder('homeBase', { diameterTop: (PAD_R + 1) * 2, diameterBottom: (PAD_R + 7) * 2, height: homeBaseH, tessellation: 9 }, scene);
  homeBase.material = stoneMat;
  homeBase.position.set(HOME.x, HOME.y - homeBaseH / 2, HOME.z);
  homeBase.convertToFlatShadedMesh();
  const home = MeshBuilder.CreateCylinder('homePad', { diameter: (PAD_R + 4) * 2, height: 2.4, tessellation: 10 }, scene);
  home.material = flatMat(scene, 'homeMat', '#a58d5f');
  home.position.set(HOME.x, HOME.y - 1.2, HOME.z);
  home.convertToFlatShadedMesh();
  home.receiveShadows = true;
  const homeSign = MeshBuilder.CreateDisc('homeSign', { radius: 5, tessellation: 24 }, scene);
  const hm = new StandardMaterial('homeSignM', scene);
  hm.diffuseTexture = numberTexture(scene, 'H', '#7a6135');
  hm.emissiveColor = new Color3(0.4, 0.36, 0.28);
  hm.specularColor = Color3.Black();
  homeSign.material = hm;
  homeSign.rotation.x = Math.PI / 2;
  homeSign.position.set(HOME.x, HOME.y + 0.06, HOME.z);

  // thin-instance bounding boxes must cover all placed instances,
  // or the GPU frustum-culls them whenever the asset origin is off-screen
  // Two things must happen after all placements: (1) upload the matrix buffer —
  // meshes with ≤32 instances and refresh=false adds otherwise NEVER upload it
  // (the >32 ones only work because the buffer overflow forces a recreate) —
  // and (2) widen the bounding box so off-origin instances aren't frustum-culled.
  for (const group of Object.values(tmpl)) {
    for (const t of group) {
      for (const m of t.meshes) {
        if (m.thinInstanceCount > 0) {
          m.thinInstanceBufferUpdated('matrix');
          m.thinInstanceRefreshBoundingInfo();
        }
      }
    }
  }

  const canopy = buildCanopy(scene, shadowDisc);
  // ONE upload for all ~910 shadow discs — it has to happen after BOTH the
  // scatter and the canopy, or the late matrices never reach the GPU and the
  // bounding box only covers the giant trees
  shadowDisc.thinInstanceBufferUpdated('matrix');
  shadowDisc.thinInstanceRefreshBoundingInfo();

  // physics colliders: cave towers + giant trunks (same cylinder shape)
  const colliders = [
    ...CAVES.map(c => ({ x: c.x, z: c.z, r: 13, topY: c.y })),
    ...canopy.trunkColliders,
  ];

  const motes = buildMotes(scene);

  return {
    shadows, colliders, crowns: canopy.crowns, caveInfo, waterMat: wm, sun, hemi,
    shafts: roof.shafts, motes, rayMat: rays.rayMat,
  };
}
